import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Monitor, Smartphone, Globe, X } from 'lucide-react';
import { API_BASE_URL } from '../utils/api';

const VERSION_RE = /^[0-9]+\.[0-9]+\.[0-9]+$/;

function PlatformToggle({ icon: Icon, label, checked, onChange }) {
  return (
    <Box
      onClick={() => onChange(!checked)}
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        px: 1.25,
        py: 1,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: checked ? 'rgba(139,92,246,0.45)' : 'rgba(255,255,255,0.08)',
        bgcolor: checked ? 'rgba(139,92,246,0.12)' : 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        transition: 'all 0.15s ease',
      }}
    >
      <Icon size={16} strokeWidth={2} color={checked ? '#a78bfa' : 'rgba(255,255,255,0.5)'} />
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: checked ? '#a78bfa' : 'text.primary' }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function BuildRunDialog({ open, onClose, onCreated, getToken, teamId }) {
  const [version, setVersion] = useState('0.0.1');
  const [windows, setWindows] = useState(true);
  const [android, setAndroid] = useState(true);
  const [pwa, setPwa] = useState(true);
  const [megaRequested, setMegaRequested] = useState(false);
  const [includeDevtools, setIncludeDevtools] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const versionValid = VERSION_RE.test(version.trim());
  const anyPlatform = windows || android || pwa;

  const handleSubmit = useCallback(async () => {
    if (!versionValid || !anyPlatform) return;
    setSubmitting(true);
    setError('');
    try {
      const token = await getToken();
      const platforms = [];
      if (windows) platforms.push('windows');
      if (android) platforms.push('android');
      if (pwa) platforms.push('pwa');
      const res = await fetch(`${API_BASE_URL}/api/builds`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teamId,
          version: version.trim(),
          platforms,
          megaRequested,
          includeDevtools,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      onCreated && onCreated();
    } catch (e) {
      setError(e.message || 'Failed to dispatch build');
    } finally {
      setSubmitting(false);
    }
  }, [versionValid, anyPlatform, getToken, teamId, version, windows, android, pwa, megaRequested, includeDevtools, onCreated]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Run build
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        disabled={submitting}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
      >
        <X size={18} />
      </IconButton>
      <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Version"
          size="small"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="0.0.1"
          error={!!version && !versionValid}
          helperText={!!version && !versionValid ? 'Format: N.N.N' : ''}
          fullWidth
        />
        <Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.75 }}>
            Platforms
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <PlatformToggle icon={Monitor} label="Windows" checked={windows} onChange={setWindows} />
            <PlatformToggle icon={Smartphone} label="Android" checked={android} onChange={setAndroid} />
            <PlatformToggle icon={Globe} label="PWA" checked={pwa} onChange={setPwa} />
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={megaRequested}
              onChange={(e) => setMegaRequested(e.target.checked)}
              sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#a78bfa' } }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.82rem' }}>
              Upload to MEGA after build (can also be triggered later from the build modal)
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={includeDevtools}
              onChange={(e) => setIncludeDevtools(e.target.checked)}
              disabled={!windows}
              sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#a78bfa' } }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.82rem', color: windows ? 'text.primary' : 'text.disabled' }}>
              Include Windows DevTools (auto-opens on launch, F12 to toggle)
            </Typography>
          }
        />
        {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !versionValid || !anyPlatform}
          sx={{ textTransform: 'none', bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {submitting ? 'Dispatching...' : 'Dispatch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
